import React from "react";
import { FiX, FiUser, FiMail, FiPhone, FiMapPin, FiUsers, FiCalendar } from "react-icons/fi";

const RELATIONSHIP_COLORS = {
  friend: { bg: "rgba(0, 150, 137, 0.15)", color: "var(--teal-bg)" },
  family: { bg: "rgba(156, 39, 176, 0.15)", color: "#9c27b0" },
  colleague: { bg: "rgba(33, 150, 243, 0.15)", color: "#2196f3" },
  client: { bg: "rgba(255, 152, 0, 0.15)", color: "#ff9800" },
  business: { bg: "rgba(76, 175, 80, 0.15)", color: "#4caf50" },
  other: { bg: "rgba(158, 158, 158, 0.15)", color: "#9e9e9e" },
};

const ViewContactModal = ({ isOpen, onClose, contact }) => {
  if (!isOpen || !contact) return null;

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ").filter((p) => p.length > 0);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return parts[0]?.charAt(0).toUpperCase() || "?";
  };

  const getRelationshipStyle = (relationship) => {
    return RELATIONSHIP_COLORS[relationship?.toLowerCase()] || RELATIONSHIP_COLORS.other;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const relStyle = getRelationshipStyle(contact.relationship);

  const InfoRow = ({ icon: Icon, label, value, value2, label2 }) => {
    if (!value && !value2) return null;
    return (
      <div style={{ marginBottom: "16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              background: "var(--input-box-bg)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={16} style={{ color: "var(--teal-bg)" }} />
          </div>
          <div style={{ flex: 1 }}>
            {value && (
              <div style={{ marginBottom: value2 ? "8px" : 0 }}>
                <div style={{ fontSize: "11px", opacity: 0.6, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>
                  {label}
                </div>
                <div style={{ fontSize: "14px", fontWeight: 500 }}>{value}</div>
              </div>
            )}
            {value2 && (
              <div>
                <div style={{ fontSize: "11px", opacity: 0.6, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>
                  {label2}
                </div>
                <div style={{ fontSize: "14px", fontWeight: 500 }}>{value2}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "var(--background)",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "480px",
          maxHeight: "90vh",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px",
            borderBottom: "1px solid var(--border-color)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, var(--teal-bg), #00b3a4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: 700,
                fontSize: "20px",
              }}
            >
              {getInitials(contact.name)}
            </div>
            <div>
              <h3 style={{ margin: 0, color: "var(--text)", fontWeight: 700, fontSize: "18px" }}>
                {contact.name}
              </h3>
              {contact.relationship && (
                <span
                  style={{
                    display: "inline-block",
                    marginTop: "6px",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: 600,
                    textTransform: "capitalize",
                    background: relStyle.bg,
                    color: relStyle.color,
                  }}
                >
                  {contact.relationship}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "8px",
              color: "var(--text)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            padding: "24px",
            overflowY: "auto",
            maxHeight: "calc(90vh - 180px)",
            color: "var(--text)",
          }}
        >
          {/* Emails */}
          <InfoRow
            icon={FiMail}
            label="Work Email"
            value={contact.work_email}
            label2="Personal Email"
            value2={contact.personal_email}
          />

          {/* Phones */}
          <InfoRow
            icon={FiPhone}
            label="Work Phone"
            value={contact.work_phone}
            label2="Personal Phone"
            value2={contact.personal_phone}
          />

          {/* Address */}
          {(contact.address_1 || contact.address_2) && (
            <InfoRow
              icon={FiMapPin}
              label="Address"
              value={[contact.address_1, contact.address_2].filter(Boolean).join(", ")}
            />
          )}

          {/* Created Date */}
          <InfoRow
            icon={FiCalendar}
            label="Added On"
            value={formatDate(contact.created_at)}
          />
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid var(--border-color)",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 24px",
              borderRadius: "10px",
              border: "none",
              background: "linear-gradient(135deg, var(--teal-bg), #00b3a4)",
              color: "white",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewContactModal;
